import { useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { FixtureCard, CountryFlag, TeamLogo, Typography } from '@dreamteam-monorepo/ui';
import type { FixtureCardProps, TeamInfo } from '@dreamteam-monorepo/ui';
import { Dropdown, DropdownOption } from '../../../../../packages/ui/src/components/Dropdown';
import { useAuthStore } from '@/stores/AuthStore';
import { useFetchMatches } from '@/hooks/useFetchMatches';
import { trackFantasyEvent } from '@/analytics';
import { useTeamCreateTournamentCalendarId } from '@/hooks/useTeamCreateTournamentCalendarId';
import { useUrlFilterState } from '@/hooks/useUrlFilterState';
import { periodAxisFromFormat } from '@/lib/periodLabels';
import type { MatchItem, FetchMatchesParams } from '@/lib/network';
import { fetchMatchdays, fetchGameweeks, fetchContestants } from '@/lib/network';
import { getMatchStatusDisplay } from '@/lib/matchStatus';
import {
  getDisplayCompetitionName,
  isMatchDetailSupportedCompetition,
} from '@/lib/competitionLabels';
import { ContentAdSlot } from '@/ads';
import { parseISO, isValid } from 'date-fns';
import { formatUkTime, formatUkDateShort, ukDateKey, formatUkDateLong } from '@/lib/ukTime';

// ─── Types from Backend API ───────────────────────────────────────────────────

interface MatchGroup {
  date: string;
  displayDate: string;
  matches: MatchItem[];
}

// ─── Helper Functions ─────────────────────────────────────────────────────────

function formatMatchTime(kickoffAt: string): string {
  const date = parseISO(kickoffAt);
  if (!isValid(date)) return '';
  return formatUkTime(kickoffAt);
}

function formatMatchDate(kickoffAt: string): string {
  const date = parseISO(kickoffAt);
  if (!isValid(date)) return '';
  return formatUkDateShort(kickoffAt);
}

function isContestantTBD(contestant: MatchItem['homeContestant']): boolean {
  return (
    !contestant.name &&
    !contestant.shortName &&
    !contestant.contestantFlagKey &&
    !contestant.shirtKey
  );
}

function getTeamLogo(contestant: MatchItem['homeContestant']): React.ReactNode {
  if (isContestantTBD(contestant)) {
    return (
      <span className="flex items-center justify-center size-[24px] rounded-full bg-[--color-neutral-200] text-[10px] font-bold text-[--color-neutral-600]">
        ?
      </span>
    );
  }
  if (contestant.contestantFlagKey) {
    return (
      <CountryFlag country={contestant.contestantFlagKey} className="size-[24px] rounded-full" />
    );
  }
  const teamCode = contestant.shortName || contestant.name;
  return <TeamLogo team={teamCode} className="size-[24px]" />;
}

// ─── Sponsor odds ─────────────────────────────────────────────────────────────

// The backend omits `odds` entirely unless there are prices it is safe to show,
// so the absence of a strip needs no further condition here.
function getOddsStrip(match: MatchItem): FixtureCardProps['oddsStrip'] {
  if (!match.odds) return undefined;
  // Fractional is nullable independently of decimal (Midnite can omit it per
  // contract), so fall back to decimal rather than rendering a blank price.
  return {
    brand: (
      <img src="/sponsors/talksportbet-mark.svg" alt="talkSPORT BET" className="h-[14px] w-auto" />
    ),
    entries: [
      {
        label: match.homeContestant.contestantFlagKey ?? match.homeContestant.shirtKey ?? 'H',
        value: match.odds.homePriceFractional ?? match.odds.homePrice,
        href: match.odds.homeClickOutUrl,
      },
      {
        label: 'DRAW',
        value: match.odds.drawPriceFractional ?? match.odds.drawPrice,
        href: match.odds.drawClickOutUrl,
      },
      {
        label: match.awayContestant.contestantFlagKey ?? match.awayContestant.shirtKey ?? 'A',
        value: match.odds.awayPriceFractional ?? match.odds.awayPrice,
        href: match.odds.awayClickOutUrl,
      },
    ],
  };
}

// Prices move fastest in play and in the hours before kick-off. Outside that
// window the server is not writing new prices, so polling would return
// identical data — refetch-on-focus already covers a player arriving.
const LIVE_POLL_WINDOW_MS = 3 * 60 * 60 * 1000;
const LIVE_POLL_INTERVAL_MS = 60 * 1000;

function shouldPollForOdds(matches: MatchItem[]): boolean {
  const now = Date.now();
  return matches.some(match => {
    if (match.status === 'playing') return true;
    if (match.status !== 'fixture') return false;
    const kickoff = parseISO(match.kickoffAt);
    if (!isValid(kickoff)) return false;
    const msToKickoff = kickoff.getTime() - now;
    return msToKickoff > 0 && msToKickoff <= LIVE_POLL_WINDOW_MS;
  });
}

// ─── Convert Match to Fixture Card Props ──────────────────────────────────────

function matchToFixtureCardProps(
  match: MatchItem,
  _index: number,
  onMatchDetailsClick: (matchId: string, matchLabel: string) => void
): FixtureCardProps {
  const hasScore = match.homeScore !== null && match.awayScore !== null;
  const statusDisplay = getMatchStatusDisplay(match.status, hasScore);

  const homeTeam: TeamInfo = {
    name: isContestantTBD(match.homeContestant) ? 'TBD' : match.homeContestant.name,
    logo: getTeamLogo(match.homeContestant),
    ...(match.homeScore !== null ? { score: match.homeScore } : {}),
  };

  const awayTeam: TeamInfo = {
    name: isContestantTBD(match.awayContestant) ? 'TBD' : match.awayContestant.name,
    logo: getTeamLogo(match.awayContestant),
    ...(match.awayScore !== null ? { score: match.awayScore } : {}),
  };

  const tournamentName = getDisplayCompetitionName(match.tournamentCalendarName);
  const matchLabel = `Match Details - ${homeTeam.name} v ${awayTeam.name}`;

  const commonProps = {
    id: match.id,
    size: 'small' as const,
    showDetails: match.matchDetailAvailable || isMatchDetailSupportedCompetition(tournamentName),
    competition: {
      label: tournamentName,
    },
    leftTeam: homeTeam,
    rightTeam: awayTeam,
    // A match renders as 'fixture' only until kick-off and as 'result' for the
    // whole in-play period, so the strip belongs on both branches.
    oddsStrip: getOddsStrip(match),
  };

  if (hasScore && statusDisplay.hasStarted) {
    return {
      ...commonProps,
      state: 'result' as const,
      venue: formatMatchDate(match.kickoffAt),
      status: statusDisplay.label,
      statusVariant: statusDisplay.statusVariant,
      detailsLabel: 'Match Details',
      onDetailsClick: () => onMatchDetailsClick(match.id, matchLabel),
    };
  }

  return {
    ...commonProps,
    state: 'fixture' as const,
    kickOff: formatMatchTime(match.kickoffAt),
    detailsLabel: 'Match Details',
    onDetailsClick: () => onMatchDetailsClick(match.id, matchLabel),
  };
}

// ─── Main Page Component ──────────────────────────────────────────────────────

// Module-level so the identity is stable across renders — useUrlFilterState
// memoises on it. `period` covers both formats; isGameweekFormat decides how
// the value is read.
const FILTER_DEFAULTS = { period: ['all'], team: ['all'] };

export default function MatchCenter() {
  const { leagueId: _leagueId = '' } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  // Filters live in the query string so they survive opening a fixture and
  // coming back, a refresh, and a shared link.
  const { values: filters, setValues: setFilters, isFromUrl } = useUrlFilterState(FILTER_DEFAULTS);
  const selectedMatchDay = filters.period;
  const selectedTeam = filters.team;

  const _isReady = useAuthStore(s => s.isReady);
  const {
    id: tournamentCalendarId,
    format: tournamentFormat,
    hasResolved: hasResolvedTournament,
  } = useTeamCreateTournamentCalendarId();
  const isGameweekFormat = periodAxisFromFormat(tournamentFormat) === 'gameweek';

  // Fetch dynamic filter data for the active tournament only. The id is part of
  // the query key so a change of active tournament refetches rather than serving
  // the previous competition's periods from cache.
  const matchdaysQuery = useQuery({
    queryKey: ['matchdays', 'list', tournamentCalendarId],
    queryFn: () => fetchMatchdays({ tournamentCalendarId: tournamentCalendarId ?? undefined }),
    enabled: !isGameweekFormat && !!tournamentCalendarId,
  });

  const gameweeksQuery = useQuery({
    queryKey: ['gameweeks', 'list', tournamentCalendarId],
    queryFn: () => fetchGameweeks({ tournamentCalendarId: tournamentCalendarId ?? undefined }),
    enabled: isGameweekFormat && !!tournamentCalendarId,
  });

  const contestantsQuery = useQuery({
    queryKey: ['contestants', 'list', tournamentCalendarId],
    queryFn: () => fetchContestants(tournamentCalendarId ?? undefined),
    enabled: !!tournamentCalendarId,
  });

  const periodLabel = isGameweekFormat ? 'Gameweek' : 'Matchday';
  const allPeriodsLabel = isGameweekFormat ? 'All Gameweeks' : 'All Matchdays';

  const matchDayOptions: DropdownOption[] = useMemo(() => {
    const base = [{ id: 'all', label: allPeriodsLabel, value: 'all' }];
    if (isGameweekFormat) {
      const gameweeks = gameweeksQuery.data?.data.gameweeks ?? [];
      return [
        ...base,
        ...gameweeks.map(gw => ({
          id: `gw${gw.number}`,
          label: gw.name ?? `Gameweek ${gw.number}`,
          value: gw.id,
        })),
      ];
    }
    const matchdays = matchdaysQuery.data?.data.matchdays ?? [];
    return [
      ...base,
      ...matchdays.map(md => ({
        id: `md${md.matchdayNumber}`,
        label: md.name,
        value: `${md.matchdayNumber}`,
      })),
    ];
  }, [gameweeksQuery.data, matchdaysQuery.data, isGameweekFormat, allPeriodsLabel]);

  const teamOptions: DropdownOption[] = useMemo(() => {
    const base = [{ id: 'all', label: 'All Teams', value: 'all' }];
    const teams = contestantsQuery.data?.data ?? [];
    return [
      ...base,
      ...teams.map(t => ({
        id: t.id,
        label: t.name,
        value: t.id,
      })),
    ];
  }, [contestantsQuery.data]);

  // Fetch matches from backend when matchday/gameweek changes.
  // Team filtering is done client-side.
  const apiParams = useMemo(() => {
    const base: FetchMatchesParams = {
      type: isGameweekFormat ? 'gameweek' : 'matchday',
      tournamentCalendarId: tournamentCalendarId ?? undefined,
    };

    const hasAll = selectedMatchDay.includes('all');
    const singlePeriod = !hasAll && selectedMatchDay.length === 1 ? selectedMatchDay[0] : null;

    if (singlePeriod) {
      if (isGameweekFormat) {
        base.gameweekId = singlePeriod;
      } else {
        const periodNum = Number(singlePeriod);
        if (!isNaN(periodNum)) {
          base.matchday = periodNum;
        }
      }
    }

    return base;
  }, [tournamentCalendarId, selectedMatchDay, isGameweekFormat]);

  // Held until the active tournament resolves, so the list never briefly shows
  // fixtures from every competition.
  const { data, isLoading } = useFetchMatches(apiParams, {
    enabled: !!tournamentCalendarId,
    // One page-level interval, set by the most urgent fixture on screen: the
    // list is a single request, so per-fixture intervals are not possible. The
    // server writes new prices at most once a minute, so polling faster returns
    // identical data. refetchIntervalInBackground stays at its default false,
    // so a hidden Gaming Hub webview stops polling.
    refetchInterval: query =>
      shouldPollForOdds(query.state.data?.data.matches ?? []) ? LIVE_POLL_INTERVAL_MS : false,
  });

  // Initialize filter to the active or locked period from the list.
  // Runs once per tournament, and never when the URL already carries a period:
  // the query data identity changes on every refetch, and re-running would
  // overwrite whatever the user picked (or came back to).
  const didInitPeriod = useRef(false);
  useEffect(() => {
    didInitPeriod.current = false;
  }, [tournamentCalendarId]);

  useEffect(() => {
    if (didInitPeriod.current || isFromUrl.period) return;

    if (isGameweekFormat) {
      const gameweeks = gameweeksQuery.data?.data.gameweeks ?? [];
      if (gameweeks.length === 0) return;
      const activeStatuses = ['open', 'locked', 'live'];
      const current = gameweeks.find(gw => activeStatuses.includes(gw.status));
      if (current) {
        didInitPeriod.current = true;
        setFilters(prev => ({ ...prev, period: [current.id] }));
      }
    } else {
      const matchdays = matchdaysQuery.data?.data.matchdays ?? [];
      if (matchdays.length === 0) return;
      const active = matchdays.find(md => md.status === 'active');
      const locked = matchdays.find(md => md.status === 'locked');
      const current = active ?? locked;
      if (current) {
        didInitPeriod.current = true;
        setFilters(prev => ({ ...prev, period: [`${current.matchdayNumber}`] }));
      }
    }
  }, [matchdaysQuery.data, gameweeksQuery.data, isGameweekFormat, isFromUrl.period, setFilters]);

  const handleMatchDayChange = (option: DropdownOption, checked: boolean) => {
    setFilters(prev => {
      const period = prev.period;
      if (option.value === 'all' || period.includes('all')) {
        return { ...prev, period: checked && option.value === 'all' ? ['all'] : [option.value] };
      }
      const newSelection = checked
        ? [...period, option.value]
        : period.filter(v => v !== option.value);
      return { ...prev, period: newSelection.length === 0 ? ['all'] : newSelection };
    });
    trackFantasyEvent('matchCentreFilter', {
      filter_name: periodLabel,
      filter_value: `${checked ? 'Select' : 'Remove'} ${option.label}`,
    });
  };

  const handleTeamChange = (option: DropdownOption, checked: boolean) => {
    setFilters(prev => {
      const team = prev.team;
      if (option.value === 'all' || team.includes('all')) {
        return { ...prev, team: checked && option.value === 'all' ? ['all'] : [option.value] };
      }
      const newSelection = checked ? [...team, option.value] : team.filter(v => v !== option.value);
      return { ...prev, team: newSelection.length === 0 ? ['all'] : newSelection };
    });
    trackFantasyEvent('matchCentreFilter', {
      filter_name: 'Team',
      filter_value: `${checked ? 'Select' : 'Remove'} ${option.label}`,
    });
  };

  const handleMatchDetailsClick = (matchId: string, matchLabel: string) => {
    trackFantasyEvent('matchCentreLink', {
      link_name: matchLabel,
    });
    navigate(`/match/${matchId}`);
  };

  // Filter and group matches locally by the viewer's timezone
  const groupedMatches = useMemo<MatchGroup[]>(() => {
    const allMatches = data?.matches ?? [];

    // Gameweek options carry gameweek ids while the list response only carries
    // the gameweek number. A single selection is filtered server-side via
    // gameweekId; several selections fetch the whole season, so map the chosen
    // ids to numbers and filter here (ENG-1168). Fixtures with no gameweek
    // (adopted cup ties) only show under "All Gameweeks".
    const gameweekList = gameweeksQuery.data?.data.gameweeks;
    const selectedGameweekNumbers =
      isGameweekFormat && gameweekList && !selectedMatchDay.includes('all')
        ? new Set(gameweekList.filter(gw => selectedMatchDay.includes(gw.id)).map(gw => gw.number))
        : null;

    // 1. Filter
    const filteredMatches = allMatches.filter(match => {
      const matchdayMatch = isGameweekFormat
        ? selectedGameweekNumbers === null ||
          (match.gameweek !== null && selectedGameweekNumbers.has(match.gameweek))
        : selectedMatchDay.includes('all') ||
          selectedMatchDay.includes(`${match.matchday ?? match.gameweek}`);

      const teamMatch =
        selectedTeam.includes('all') ||
        selectedTeam.some(id => match.homeContestant.id === id || match.awayContestant.id === id);

      return matchdayMatch && teamMatch;
    });

    // 2. Group by local date
    const groups = new Map<string, MatchGroup>();

    for (const match of filteredMatches) {
      const localDate = parseISO(match.kickoffAt);
      if (!isValid(localDate)) continue;

      // Group by the UK calendar day so fixtures land on the right date header.
      const dateKey = ukDateKey(match.kickoffAt);
      const displayDate = formatUkDateLong(match.kickoffAt);

      if (!groups.has(dateKey)) {
        groups.set(dateKey, { date: dateKey, displayDate, matches: [] });
      }
      const group = groups.get(dateKey);
      if (group) {
        group.matches.push(match);
      }
    }

    // 3. Sort by date ascending
    return Array.from(groups.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [data, gameweeksQuery.data, isGameweekFormat, selectedMatchDay, selectedTeam]);

  return (
    <div className="components light flex flex-col max-w-[1281px] mx-auto w-full min-h-screen bg-[#f5f5f5]">
      {/* Page Header */}
      <div className="bg-white w-full">
        <div className="max-w-[1280px] mx-auto w-full flex flex-col gap-[--padding-md] md:gap-[--padding-xl] pt-[--padding-xl] pb-[--padding-md] md:pb-[--padding-2xl] px-[--padding-md]">
          <div className="flex flex-col gap-[--padding-md] md:flex-row   md:justify-between">
            <Typography
              as="h1"
              variant="headline050"
              className="text-[--page-header-header] !font-[900] shrink-0 md:!text-40 md:!leading-44"
            >
              MATCH CENTRE
            </Typography>

            <div className="flex gap-[--padding-md] w-full md:w-auto md:shrink-0">
              <Dropdown
                label={periodLabel}
                options={matchDayOptions}
                values={selectedMatchDay}
                multiple
                onWhite
                onValueChange={handleMatchDayChange}
                className="flex-1 md:w-40 md:flex-none"
              />
              <Dropdown
                label="Team"
                options={teamOptions}
                values={selectedTeam}
                multiple
                onWhite
                onValueChange={handleTeamChange}
                className="flex-1 md:w-40 md:flex-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Matches Grid by Date */}
      <div className="flex-1 px-4 py-6 bg-white">
        {!hasResolvedTournament ||
        isLoading ||
        matchdaysQuery.isLoading ||
        gameweeksQuery.isLoading ||
        contestantsQuery.isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Typography variant="paragraph020" className="text-[--color-neutral-600]">
              Loading matches...
            </Typography>
          </div>
        ) : groupedMatches.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <Typography variant="paragraph020" className="text-[--color-neutral-600]">
              No matches found for selected filters
            </Typography>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {groupedMatches.map(group => (
              <section key={group.date} className="flex flex-col gap-4">
                {/* Date Header */}
                <Typography
                  as="h2"
                  variant="subheadline050"
                  className="text-[--color-neutral-900] font-bold"
                >
                  {group.displayDate}
                </Typography>

                {/* Cards Grid - 2 columns on tablet, 3 on desktop */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.matches.map((match, index) => (
                    <FixtureCard
                      key={match.id}
                      {...matchToFixtureCardProps(match, index, handleMatchDetailsClick)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
        <ContentAdSlot id="ad-boa-leaderboard" className="px-0 pb-0" />
      </div>
    </div>
  );
}
